// W-0533 — Manutenção de Títulos (GES)
// Filtros → Grid → Detalhe + Painel de Processamento
import React, { useState, useCallback, useRef } from 'react';
import { Container, Card, Pagination, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import { buscarTitulosManutencao, obterDetalheTituloManutencao } from '~/Services/Service';
import FiltrosBusca from './components/FiltrosBusca';
import DetalheTitulo from './components/DetalheTitulo';
import PainelProcessamento from './components/PainelProcessamento';

const PAGE_SIZE = 20;

const formatarValor = (valor) => {
  if (valor == null) return '-';
  return Number(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const normalizarTitulos = (lista) =>
  lista.map((item) => ({
    ...item,
    id: item.codigo_titulo,
  }));

const ManutencaoTitulos = () => {
  const [grupoEmpresa, setGrupoEmpresa] = useState(null);
  const [titulos, setTitulos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtroAtual, setFiltroAtual] = useState(null);
  const [carregandoBusca, setCarregandoBusca] = useState(false);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [tituloSelecionado, setTituloSelecionado] = useState(null);
  const detalheRequestId = useRef(0);

  const totalPaginas = Math.ceil(totalRegistros / PAGE_SIZE) || 1;

  const buscar = useCallback(async (filtro, paginaNum) => {
    setCarregandoBusca(true);
    detalheRequestId.current += 1;
    setCarregandoDetalhe(false);
    setTituloSelecionado(null);
    const payload = {
      ...filtro,
      pagina: paginaNum || filtro.pagina || 1,
      tamanho_pagina: PAGE_SIZE,
    };
    try {
      const res = await buscarTitulosManutencao(payload);
      const lista = Array.isArray(res) ? res : (res && res.itens ? res.itens : []);
      const total = res && res.total_registros != null ? res.total_registros : lista.length;
      setTitulos(normalizarTitulos(lista));
      setTotalRegistros(total);
      if (lista.length === 0) {
        toast.info('Nenhum título encontrado ou título já baixado.');
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao buscar títulos.';
      toast.error(msg);
      setTitulos([]);
      setTotalRegistros(0);
    } finally {
      setCarregandoBusca(false);
    }
  }, []);

  const handleBuscar = (filtro) => {
    setFiltroAtual(filtro);
    setPagina(1);
    buscar(filtro, 1);
  };

  const handlePageChange = (novaPagina) => {
    if (!filtroAtual) return;
    setPagina(novaPagina);
    buscar(filtroAtual, novaPagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFecharDetalhe = useCallback(() => {
    detalheRequestId.current += 1;
    setCarregandoDetalhe(false);
    setTituloSelecionado(null);
  }, []);

  const handleSelecionarTitulo = useCallback(async (codigoTitulo) => {
    const requestId = detalheRequestId.current + 1;
    detalheRequestId.current = requestId;

    setTituloSelecionado(null);
    setCarregandoDetalhe(true);
    try {
      const detalhe = await obterDetalheTituloManutencao(codigoTitulo);
      if (detalheRequestId.current !== requestId) return;
      setTituloSelecionado(detalhe);
    } catch (err) {
      if (detalheRequestId.current !== requestId) return;
      const msg = err && err.message ? err.message : 'Erro ao carregar detalhe do título.';
      toast.error(msg);
    } finally {
      if (detalheRequestId.current === requestId) {
        setCarregandoDetalhe(false);
      }
    }
  }, []);

  const handleProcessado = useCallback(() => {
    handleFecharDetalhe();
    if (filtroAtual) buscar(filtroAtual, pagina);
  }, [buscar, filtroAtual, handleFecharDetalhe, pagina]);

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const paginas = [];
    const maxBotoes = 5;
    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) inicio = Math.max(1, fim - maxBotoes + 1);

    paginas.push(
      <Pagination.First key="first" disabled={pagina === 1} onClick={() => handlePageChange(1)} />,
      <Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => handlePageChange(pagina - 1)} />
    );
    if (inicio > 1) paginas.push(<Pagination.Ellipsis key="ell-start" disabled />);
    for (let i = inicio; i <= fim; i += 1) {
      paginas.push(
        <Pagination.Item key={i} active={i === pagina} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) paginas.push(<Pagination.Ellipsis key="ell-end" disabled />);
    paginas.push(
      <Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => handlePageChange(pagina + 1)} />,
      <Pagination.Last key="last" disabled={pagina === totalPaginas} onClick={() => handlePageChange(totalPaginas)} />
    );

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {Math.min((pagina - 1) * PAGE_SIZE + 1, totalRegistros)} até{' '}
          {Math.min(pagina * PAGE_SIZE, totalRegistros)} de {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {paginas}
        </Pagination>
      </div>
    );
  };

  const colunas = [
    { key: 'codigo_titulo', title: 'Codigo', ordenacao: true },
    { key: 'numero_duplicata', title: 'Duplicata' },
    { key: 'banco_cheque', title: 'Banco' },
    { key: 'agencia_cheque', title: 'Agencia' },
    { key: 'conta_cheque', title: 'Conta' },
    { key: 'numero_cheque', title: 'Cheque' },
    { key: 'valor', title: 'Valor', align: 'right', render: formatarValor },
    { key: 'cgc_cpf', title: 'CPF/CNPJ', ordenacao: true },
    { key: 'conta_portador', title: 'Portador', ordenacao: true },
    { key: 'codigo_grupo_empresa', title: 'G.E.' },
    { key: 'codigo_boleto', title: 'Boleto' },
    { key: 'parcela', title: 'Parcela' },
    { key: 'codigo_proposta', title: 'Proposta', ordenacao: true },
  ];

  return (
    <Container fluid className="py-3">
      <Header title="Manutenção de Títulos" />

      <Card className="shadow-sm mt-3 mb-3">
        <Card.Body className="py-3">
          <FiltrosBusca
            grupoEmpresa={grupoEmpresa}
            onGrupoEmpresaChange={setGrupoEmpresa}
            onBuscar={handleBuscar}
            carregando={carregandoBusca}
          />
        </Card.Body>
      </Card>

      {carregandoBusca && <Loading />}

      {!carregandoBusca && titulos.length > 0 && (
        <Card className="shadow-sm mb-3">
          <Card.Header
            className="d-flex justify-content-between align-items-center py-2"
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
          >
            <strong>
              Títulos encontrados
              <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                ({totalRegistros} {totalRegistros === 1 ? 'registro' : 'registros'})
              </span>
            </strong>
          </Card.Header>
          <Card.Body className="p-0">
            <div style={{ overflowX: 'auto' }}>
              <TabelaDinamica
                colunas={colunas}
                dados={titulos}
                striped
                hover
                cliqueLinha={(item) => handleSelecionarTitulo(item.codigo_titulo)}
                linhaSelecionada={(item) =>
                  tituloSelecionado && tituloSelecionado.codigo_titulo === item.codigo_titulo
                }
              />
            </div>
            {renderPaginacao()}
          </Card.Body>
        </Card>
      )}

      <Modal
        show={carregandoDetalhe || !!tituloSelecionado}
        onHide={handleFecharDetalhe}
        size="xl"
        backdrop="static"
        scrollable
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>
            {tituloSelecionado
              ? `Título ${tituloSelecionado.codigo_titulo}`
              : 'Carregando título...'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {carregandoDetalhe && <Loading label="Carregando título..." />}

          {!carregandoDetalhe && tituloSelecionado && (
            <>
              <DetalheTitulo titulo={tituloSelecionado} />
              <PainelProcessamento
                titulo={tituloSelecionado}
                codigoGrupoEmpresa={grupoEmpresa ? grupoEmpresa.value : null}
                onProcessado={handleProcessado}
              />
            </>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ManutencaoTitulos;
